import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, Lock, RefreshCcw, Loader } from "lucide-react";
import VRSOrbitLogo from "../../Assets/vrs orbit logo.png";
import avatar from "../../Assets/avatar.png";
import desk from "../../Assets/rafiki.png";
import leftBg from "../../Assets/left-bg.png";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { OrbitLogin } from "../../Services/LoginService";
import { getCaptchaService } from "../../Services/ToolKitService";
import NotificationAlert from "../Alerts/NotificationAlert";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [pageLoaded, setPageLoaded] = useState(false);

  //  Alert state
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    // Check Email & Password Empty
    if (!email.trim() || !password.trim()) {
      setAlert({
        show: true,
        type: "warning",
        message: "Please enter both email and password.",
      });
      return;
    } else {
      // Password Policy Check
      const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

      if (!passwordRegex.test(password)) {
        setAlert({
          show: true,
          type: "error",
          message:
            "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character.",
        });
        return;
      }
    }
    // Captcha Empty Check
    if (!captchaInput.trim()) {
      setAlert({
        show: true,
        type: "warning",
        message: "Please enter captcha.",
      });
      return;
    } else {
      // Captcha Check
      if (
        captchaInput.toUpperCase() === captchaText.toUpperCase() &&
        captchaInput !== captchaText
      ) {
        setAlert({
          show: true,
          type: "warning",
          message: "Captcha is case-sensitive. Please enter exactly as shown!",
        });

        getCaptcha();
        setCaptchaInput("");
        return;
      }

      if (captchaInput.toUpperCase() !== captchaText.toUpperCase()) {
        setAlert({
          show: true,
          type: "error",
          message: "Incorrect captcha. Please try again!",
        });

        getCaptcha();
        setCaptchaInput("");
        return;
      }
    }
    setLoading(true);

    try {
      const result = await OrbitLogin(email, password, keepMeLoggedIn);
      console.log("Login Response:", result);

      if (result?.status === "Success") {
        const token = result.token?.token;

        if (token) {
          Cookies.set("Orbit_Token", token, {
            secure: true,
            sameSite: "Strict",
            expires: keepMeLoggedIn ? 30 : 1 / 3,
          });
        }

        setAlert({
          show: true,
          type: "success",
          message: result.message || "Login successful! Redirecting...",
        });

        setTimeout(() => navigate("/home"), 1000);
      } else {
        setAlert({
          show: true,
          type: "error",
          message: result.message || "Invalid credentials.",
        });
        getCaptcha();
      }
    } catch (error) {
       if (error.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: error.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      setAlert({
        show: true,
        type: "error",
        message: "Server error, please try again!",
      });
    } finally {
      setCaptchaInput("");
      setLoading(false);
    }
  };

  const getCaptcha = async () => {
    try {
      const response = await getCaptchaService();
      const data = response.data;

      setCaptchaImage(data.captchaImage);
      setCaptchaText(data.captchaText);
      setCaptchaInput("");
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error(err);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to load captcha. Try again!",
      });
    }
  };

  useEffect(() => {
    getCaptcha();
  }, []);

  useEffect(() => {
    setTimeout(() => setPageLoaded(true), 100);
  }, []);

  return (
    <div className="min-h-screen flex flex-col md:flex-row relative overflow-hidden">
      <NotificationAlert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        duration={2500}
        onClose={() => setAlert({ ...alert, show: false })}
      />

      {/* Left Side */}
      <div
        className={`hidden md:flex flex-col items-center justify-center w-1/2 text-white p-10 relative overflow-hidden transition-all duration-700 ease-out
         ${pageLoaded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}
         `}
        style={{
          backgroundImage: `url(${leftBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 text-center">
          <img
            src={avatar}
            alt="Avatar"
            className="mx-auto w-30 h-20 mb-[43px]"
          />
          <h1 className="text-3xl font-bold mb-2">WELCOME BACK!</h1>
          <p className="text-lg mb-10">
            To keep connected with us <br /> please login with your personal
            info
          </p>
          <img
            src={desk}
            alt="Desk Illustration"
            className="mx-auto w-[450px] mt-6"
          />
        </div>
      </div>

      {/* Right Side */}
      <div
        className={`flex flex-col justify-center items-center w-full md:w-1/2 bg-white p-8
         transition-all duration-700 ease-out delay-150
         ${pageLoaded ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}
       `}
      >
        <div
          className={`max-w-sm w-full transition-all duration-700 delay-300
          ${pageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
        >
          {/* Logo */}
          <div className="flex items-center justify-center mb-[80px]">
            <img
              src={VRSOrbitLogo}
              alt="VRS Orbit"
              className="h-100 mr-2 w-[450px]"
            />
          </div>

          <h3 className="text-center text-lg font-semibold mb-[40px]">
            Login in to your account to continue
          </h3>

          <form className="space-y-4" onSubmit={handleLogin}>
            {/* Email */}
            <label>Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="email"
                placeholder="name@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-[400px] pl-10 pr-3 py-2 mb-[34px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]"
              />
            </div>

            {/* Password */}
            <label>Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-[400px] mb-6 pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Captcha */}
            <label>Captcha</label>
            <div className="flex items-center gap-3 mb-[25px]">
              {/* Captcha Image */}
              <div
                className="cursor-pointer rounded-md flex items-center justify-center shadow-sm"
                onClick={getCaptcha}
              >
                {captchaImage ? (
                  <img
                    src={captchaImage}
                    alt="Captcha"
                    className="h-[45px] w-auto select-none"
                    draggable="false"
                  />
                ) : (
                  <div className="h-[45px] w-[120px] flex justify-center items-center">
                    <Loader className="animate-spin text-gray-500" size={22} />
                  </div>
                )}
              </div>

              {/* Refresh Icon */}
              <button
                type="button"
                onClick={getCaptcha}
                title="Refresh Captcha"
                className="text-[#0061A4] hover:text-[#004a80] transition p-1"
              >
                <RefreshCcw size={22} />
              </button>

              {/* Captcha Input */}
              <input
                type="text"
                placeholder="Enter code"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                className="w-[200px] pl-3 pr-2 py-2 border border-gray-300 rounded-md
               focus:outline-none focus:ring-2 focus:ring-[#007BFF] text-sm"
              />
            </div>

            {/* Keep Me Logged In */}
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={keepMeLoggedIn}
                  onChange={() => setKeepMeLoggedIn(!keepMeLoggedIn)}
                  className="accent-[#0061A4] cursor-pointer"
                />
                <span>Keep me logged in</span>
              </label>
              <a
                href="#"
                className="text-[#0061A4] text-sm hover:underline font-medium"
              >
                Forgot your password?
              </a>
            </div>

            {/* Login Button */}
            <div className="flex justify-center mt-4">
              <button
                type="submit"
                disabled={loading}
                className={`w-[120px] ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#0061A4] to-[#00ACED]"
                } text-white py-2 rounded-md font-medium hover:opacity-90 transition`}
              >
                {loading ? "Loading..." : "Login"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
